import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { MdfFieldEntity } from '../mdf/fieldDefinition';
import type { LayoutSettings, ViewType } from './mdfModels';
import type { Progression } from '../upload/uploadProgression';
import type { ItemMetaData } from './fieldValue';
import type { AnalyticConfig } from './tenantModel';
import { getVariableFromContextEnvironment } from '../runtime/contextEnvironmentVariables';

/**
 * Note: This file has DTOs for share requests done from inside Mimir,
 * see sharePublicDtos for the types used in the public share API.
 */


export type ShareType = 'share' | 'share_enhanced';

export interface ExternalShareGetDto {
  secret: string;
  title: string;

  itemIds: string[];
  folderIds: string[];

  owner: string;

  // ISO timestamp of when the share was created.
  createdOn: string;

  // ISO timestamp of the share expiration time, after which it may no longer be used.
  // If null the share has no expiration time.
  // TODO: Remove null after setting validTo on all shares
  validTo: string | null;

  // On/off setting for the share
  enabled: boolean;

  // type of share, 'share_enhanced' is used for external users accessing and mutating data
  shareType?: ShareType | undefined;

  // List of specific email addresses allowed to access this share
  // If empty, any external user can access (subject to other restrictions)
  externalUsers?: ShareExternalUser[];

  // A log of sent emails for the share
  emailHistory: {
    emailAdr: string;
    isoTimestamp: string;
  }[];

  // The number of views is counted by how many times
  // a new token has been obtained for the share.
  views: number;

  // The maximum number allowed for `share.views`.
  // When maxViews=views, no new tokens are served out for the share.
  maxExternalViews: number;

  // The permissions granted to the external users accessing the share
  permissions: SharePermissions[];

  // The mdf type to fill upon folder upload with edit_metadata permission,
  // defaultMetadata is the replacement of this field. It'll be removed in future
  formId?: string

  // Wether or not the share includes subfolders
  includeSubfolders?: boolean;

  hasPassword: boolean;

  // The prefilled metadata filled by requester of the share, which uploader can override with edit_metadata permission
  // This property replace the functionality of formId and only be used with folder uploads
  defaultMetaData?: ItemMetaData;

  // The default analytics configuration for video uploads through this share
  defaultVideoAnalytics?: Partial<AnalyticConfig>;

  // The default analytics configuration for audio uploads through this share
  defaultAudioAnalytics?: Partial<AnalyticConfig>;

  // The default analytics configuration for image uploads through this share
  defaultImageAnalytics?: Partial<AnalyticConfig>;
}

export interface UpdateExternalShareDto extends Partial<ExternalShareGetDto> {
  // undefined means no change, null means remove password
  password?: string | null;
}

export interface ShareExternalUser {
  email: string;
  externalUserId: string;
}

export interface ShareCreationRequestDto {
  title: string;
  validTo: string;
  permissions: SharePermissions[];
  message: string;
  enabled: boolean;
  itemIds: string[];
  folderIds: string[];

  // type of share, 'share_enhanced' is used for external users accessing and mutating data
  shareType?: ShareType;
    // used for share_enhanced type shares
  externalUsers?: ShareExternalUser[];

  defaultMetaData?: ItemMetaData;
  defaultVideoAnalytics?: Partial<AnalyticConfig>;
  defaultAudioAnalytics?: Partial<AnalyticConfig>;
  defaultImageAnalytics?: Partial<AnalyticConfig>;
}

export interface ShareUpdateRequestDto {
  title?: string;
  validTo?: string;
  permissions?: SharePermissions[];
  enabled?: boolean;
  emailsToAdd?: string[];
  includeSubfolders?: boolean;

  // If any emails are included in the payload, this
  // message will be included in the sent emails.
  // This value is not persisted.
  message?: string;
  formId?: string;
  // undefined means no change, null means remove password
  password?: string | null;

  // List of specific email addresses allowed to access this share
  externalUsers?: ShareExternalUser[];

  defaultMetaData?: ItemMetaData;

  // Default analytics configuration for uploads through this share
  defaultVideoAnalytics?: Partial<AnalyticConfig>;
  defaultAudioAnalytics?: Partial<AnalyticConfig>;
  defaultImageAnalytics?: Partial<AnalyticConfig>;
}

// Upload progress for placeholder- or folder upload.
export type ShareUploadProgressDto =
  UploadProgressBytesDto |
  UploadErrorDto;

export interface UploadProgressBytesDto {
  type: 'bytes';
  // Progress keyed by item id.
  progress: Record<string, {
    bytes: Progression;

    // For a multipart upload, even though all bytes of the resource
    // have been uploaded, it is only done once the complete-multipart
    // request completes.
    // And even for a single PUT request, the upload progress reporting
    // (e.g., by axios) may report all bytes as uploaded before the
    // the request is fully complete (i.e., the request may still fail after
    // all bytes have been sent)
    ended: boolean;
  }>,

  // The id the client used when initiating the upload,
  // and which is used to identify the upload lock.
  clientSessionId: string;
}

// Included as part of some errors thrown by the share API,
// to allow the share page to show a Mimir link to the content.
export interface FailedShareContentInfo {
  itemIds: string[];
  folderIds: string[];
}

/**
 * Indicates the client has given up on the upload to the
 * given items.
 */
export interface UploadErrorDto {
  type: 'error';
  itemIds: string[];
  errorMsg: string;
}

// The dto for a mdf schema sent to the share page, where we
// expose the subset of the schema relevant for the share page.
export type MdfSchemaShareDto = {
  id: string;
  label: string;
  rule?: string;
  active: boolean;
  views: {
    [ViewType.SHARE]: LayoutSettings[];
  };
  fields: MdfFieldEntity[];
  displayName?: string;
};

export const CONTENT_ID_PARAM = 'contentId';
export const ALL_FOR_TENANT_PARAM = 'tenant';
export type SharePermissions =
  | 'downloadHighRes'
  | 'downloadEditProxy'
  | 'downloadWebProxy'
  | 'read_comment'
  | 'write_comment'
  | 'view_metadata'
  | 'edit_metadata'
  | 'view_transcript'
  | 'edit_transcript'
  | 'upload';

export function isShareCreationRequestDto(dto: unknown): dto is ShareCreationRequestDto {
  if (!isObject(dto)) return false;

  const maybe: Partial<ShareCreationRequestDto> = dto;
  return (typeof maybe.title === 'string') &&
    (maybe.validTo === null || !isNaN(new Date(maybe.validTo ?? '').getTime())) &&
    (Array.isArray(maybe.permissions));
}

export function isShareUpdateRequestDto(dto: unknown): dto is ShareUpdateRequestDto {
  if (!isObject(dto)) return false;

  const maybe: Partial<ShareUpdateRequestDto> = dto;
  return (maybe.title === undefined || typeof maybe.title === 'string') &&
    (!maybe.validTo || !isNaN(new Date(maybe.validTo ?? '').getTime())) &&
    (maybe.permissions === undefined || Array.isArray(maybe.permissions)) &&
    (maybe.emailsToAdd === undefined || Array.isArray(maybe.emailsToAdd));
}

export const generateShareLink = (secret: string, baseUrl: string, isUploadRequest = false) => {
  const path = isUploadRequest ? 'upload' : 'shares';
  if (getVariableFromContextEnvironment('NODE_ENV') === 'development') {
    const url = new URL(baseUrl);
    return `${url.protocol}//${url.hostname}:8080/${path}/${secret}`;
  }
  if (baseUrl.endsWith('/')) baseUrl = baseUrl.substring(0, baseUrl.length - 1);
  return `${baseUrl}/${path}/${secret}`;
};
